import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { ImgHTMLAttributes, SyntheticEvent } from 'react';
import { assetUrl } from '../asset-url';

// Port of src/app/directives/image-loader.directive.ts ([imageLoader]).
//
// Directive behavior (mirrored exactly):
// - While the image loads, an overlay is shown BEFORE the <img> in the DOM:
//   <div class="absolute inset-0 bg-accent/10 animate-pulse" style="z-index:1">
//     <div class="absolute inset-0 flex items-center justify-center">
//       <div class="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin">
//   The directive injected these siblings via Renderer2; here they are rendered
//   conditionally with the same classes.
// - The directive forced the img's PARENT to position: relative so the overlay
//   covers the image area — replicated via useLayoutEffect on the parentElement.
// - On 'error': src is swapped to defaultImage (default 'assets/missing-song.svg').
// - On 'load' with checkPlaceholder: if naturalWidth === 120 && naturalHeight === 90
//   the image is considered a placeholder (YouTube's gray default thumb) and is
//   swapped to defaultImage. The overlay is removed in both cases.
//
// Angular call sites used [imageLoader] with optional [defaultImage] /
// [checkPlaceholder]; everything else (class, alt, loading, fetchpriority,
// width, height, src) passes straight through to the <img>.

export interface AppImageProps extends ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  defaultImage?: string;
  checkPlaceholder?: boolean;
}

export default function AppImage({
  src,
  defaultImage = 'assets/missing-song.svg',
  checkPlaceholder = false,
  onLoad,
  onError,
  style,
  ...imgProps
}: AppImageProps) {
  const imgRef = useRef<HTMLImageElement>(null);
  const [displaySrc, setDisplaySrc] = useState(src);
  const [isLoading, setIsLoading] = useState(true);

  // Reset when the source changes (e.g. theme-dependent logos).
  useEffect(() => {
    setDisplaySrc(src);
    setIsLoading(true);
  }, [src]);

  // The directive set the parent container to position: relative so the
  // absolutely-positioned loading overlay covers the image area.
  useLayoutEffect(() => {
    const parent = imgRef.current?.parentElement;
    if (parent) {
      parent.style.position = 'relative';
    }
  }, []);

  const handleLoad = (event: SyntheticEvent<HTMLImageElement>) => {
    if (checkPlaceholder) {
      const img = event.currentTarget;
      const isPlaceholder = img.naturalWidth === 120 && img.naturalHeight === 90;
      if (isPlaceholder) {
        setDisplaySrc(defaultImage);
      }
    }
    setIsLoading(false);
    onLoad?.(event);
  };

  const handleError = (event: SyntheticEvent<HTMLImageElement>) => {
    setDisplaySrc(defaultImage);
    setIsLoading(false);
    onError?.(event);
  };

  return (
    <>
      {isLoading && (
        <div className="absolute inset-0 bg-accent/10 animate-pulse" style={{ zIndex: 1 }}>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          </div>
        </div>
      )}
      {/* While loading, alt text renders in the img's text color (Firefox
          shows it for in-flight images, not just broken ones) — make it
          transparent so only the loading overlay is visible until onLoad. */}
      <img ref={imgRef}
           src={assetUrl(displaySrc)}
           onLoad={handleLoad}
           onError={handleError}
           style={isLoading ? { ...style, color: 'transparent' } : style}
           {...imgProps} />
    </>
  );
}
